import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { getCommunityUsers, updateUserRole, removeCommunityUser } from '../../services/communityService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserShield,
  faUser,
  faUserMinus,
  faSearch,
  faUserPlus,
  faEllipsisV,
  faCrown,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import UserSearchInvite from './UserSearchInvite';

function CommunityUsersList({ communityId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showOptionsFor, setShowOptionsFor] = useState(null);
  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const { user } = useAuth();
  const { isMobile } = useNavigation();

  useEffect(() => {
    loadUsers();
  }, [communityId]);

  const loadUsers = async () => {
    if (!communityId) {
      setError('No community ID provided');
      return;
    }
    
    setLoading(true);
    try {
      const data = await getCommunityUsers(communityId);
      setUsers(data);
      setError('');
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (!window.confirm(`Ali ste prepričani, da želite spremeniti vlogo uporabnika?`)) {
      return;
    }
  
    setLoading(true);
    try {
      await updateUserRole(communityId, userId, newRole);
      // Update user in local state
      setUsers(users.map(u => u.id === userId ? {...u, role: newRole} : u));
      setError('');
      // Close options menu
      setShowOptionsFor(null);
    } catch (err) {
      setError('Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Ali ste prepričani, da želite odstraniti tega uporabnika iz skupine?')) {
      return;
    }
  
    setLoading(true);
    try {
      await removeCommunityUser(communityId, userId);
      // Remove user from local state
      setUsers(users.filter(u => u.id !== userId));
      setError('');
      // Close options menu
      setShowOptionsFor(null);
    } catch (err) {
      setError('Failed to remove user from community');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteSent = (invitedUser) => {
    // Refresh the users list after an invite is sent
    loadUsers();
  };

  const toggleInvitePanel = () => {
    setShowInvitePanel(!showInvitePanel);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close options menu when clicking outside
  const handleOutsideClick = () => {
    if (showOptionsFor) {
      setShowOptionsFor(null);
    }
  };

  // MOBILE VIEW
  if (isMobile) {
    return (
      <div className="community-users">
        {error && <div className="alert alert-danger p-2 mb-3">{error}</div>}

        {/* Invite Panel */}
        {showInvitePanel && (
          <div className="card mb-3 bg-dark border-0">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 text-light">Povabi uporabnika</h6>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={toggleInvitePanel}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <UserSearchInvite
                communityId={communityId}
                onInviteSent={handleInviteSent}
                onClose={toggleInvitePanel}
              />
            </div>
          </div>
        )}

        <div className="card mb-3 bg-dark border-0">
          <div className="card-body p-2">
            <div className="input-group">
              <span className="input-group-text bg-dark border-secondary text-light">
                <FontAwesomeIcon icon={faSearch} />
              </span>
              <input
                type="text"
                className="form-control bg-dark border-secondary text-light"
                placeholder="Isci po imenu ali e-posti..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                className={`btn ${showInvitePanel ? 'btn-secondary' : 'btn-primary'}`}
                onClick={toggleInvitePanel}
                title="Povabi uporabnika"
              >
                <FontAwesomeIcon icon={showInvitePanel ? faTimes : faUserPlus} />
              </button>
            </div>
          </div>
        </div>

        {showOptionsFor && (
          <div className="mobile-backdrop" onClick={handleOutsideClick}></div>
        )}
        
        {loading && !users.length ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Nalaganje...</span>
            </div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="mobile-song-empty">
            <p className="text-muted py-4">
              {searchTerm ? 'Ni najdenih uporabnikov' : 'Ni uporabnikov v tej skupini'}
            </p>
          </div>
        ) : (
          <div className="mobile-song-list">
            {filteredUsers.map(communityUser => (
              <div key={communityUser.id} className="mobile-song-item">
                <div className="mobile-song-item-content">
                  <div className="mobile-song-icon">
                    {communityUser.role === 'community_admin' ? (
                      <FontAwesomeIcon icon={faCrown} className="text-warning" />
                    ) : (
                      <FontAwesomeIcon icon={faUser} />
                    )}
                  </div>
                  <div className="mobile-song-details">
                    <div className="mobile-song-title">{communityUser.display_name || communityUser.email?.split('@')[0]}</div>
                    <div className="mobile-song-meta">
                      <div className="mobile-song-composer text-truncate">
                        {communityUser.email}
                      </div>
                    </div>
                  </div>
                  
                  {communityUser.id !== user.id && (
                    <button 
                      className="mobile-song-options"
                      onClick={() => setShowOptionsFor(communityUser.id === showOptionsFor ? null : communityUser.id)}
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </button>
                  )}
                </div>
                
                {showOptionsFor === communityUser.id && (
                  <div className="mobile-song-options-menu">
                    {communityUser.role === 'community_admin' ? (
                      <button onClick={() => handleRoleChange(communityUser.id, 'member')}>
                        <FontAwesomeIcon icon={faUser} className="me-2" />
                        Nastavi kot član
                      </button>
                    ) : (
                      <button onClick={() => handleRoleChange(communityUser.id, 'community_admin')}>
                        <FontAwesomeIcon icon={faUserShield} className="me-2" />
                        Nastavi kot admin
                      </button>
                    )}
                    <button className="delete-option" onClick={() => handleRemoveUser(communityUser.id)}>
                      <FontAwesomeIcon icon={faUserMinus} className="me-2" />
                      Odstrani iz skupine
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // DESKTOP VIEW
  return (
    <div className="community-users">
      <div className="mb-3">
        <h4>Uporabniki skupine</h4>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Invite Panel */}
      {showInvitePanel && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Povabi uporabnika</h5>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={toggleInvitePanel}
              >
                <FontAwesomeIcon icon={faTimes} /> Zapri
              </button>
            </div>
            <UserSearchInvite
              communityId={communityId}
              onInviteSent={handleInviteSent}
              onClose={toggleInvitePanel}
            />
          </div>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <div className="input-group mb-3">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Isci po uporabniskem imenu ali e-posti..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              className={`btn ${showInvitePanel ? 'btn-secondary' : 'btn-primary'}`}
              onClick={toggleInvitePanel}
              title="Povabi uporabnika"
            >
              <FontAwesomeIcon icon={showInvitePanel ? faTimes : faUserPlus} />
              <span className="ms-2 d-none d-md-inline">
                {showInvitePanel ? 'Zapri' : 'Povabi'}
              </span>
            </button>
          </div>
          
          {loading && !users.length ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Nalaganje...</span>
              </div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <p className="text-center text-muted py-4">
              {searchTerm ? 'Ni najdenih uporabnikov' : 'Ni uporabnikov v tej skupini'}
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Prikazno ime</th>
                    <th>E-pošta</th>
                    <th>Vloga</th>
                    <th>Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(communityUser => (
                    <tr key={communityUser.id}>
                      <td>{communityUser.display_name || communityUser.email?.split('@')[0]}</td>
                      <td>{communityUser.email}</td>
                      <td>
                        <span className={`badge ${communityUser.role === 'community_admin' ? 'bg-primary' : 'bg-secondary'}`}>
                          {communityUser.role === 'community_admin' ? 'Admin' : 'Član'}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group">
                          {communityUser.id !== user.id && (
                            <>
                              {communityUser.role === 'community_admin' ? (
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => handleRoleChange(communityUser.id, 'member')}
                                  title="Odstrani admin pravice"
                                >
                                  <FontAwesomeIcon icon={faUser} />
                                </button>
                              ) : (
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleRoleChange(communityUser.id, 'community_admin')}
                                  title="Nastavi kot admin"
                                >
                                  <FontAwesomeIcon icon={faUserShield} />
                                </button>
                              )}
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleRemoveUser(communityUser.id)}
                                title="Odstrani iz skupine"
                              >
                                <FontAwesomeIcon icon={faUserMinus} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CommunityUsersList;